import React, { useMemo } from "react";
import ReactFlow, { Background, Controls, MiniMap, MarkerType } from "reactflow";
import "reactflow/dist/style.css";

/**
 * Project 11: Multi-Tenant Keycloak IAM Modernisation (KC15 -> KC26)
 * Fleet migration of ~30 tenant SSO from Keycloak 15/Wildfly to Keycloak 26/Quarkus
 * on ARM Graviton with blue-green cutovers and checksum-verified realm parity.
 */

const nodeStyle = {
  section: {
    background: "#ffffff",
    border: "2px solid #cbd5e1",
    borderRadius: 12,
    padding: 6,
    fontWeight: 800,
    fontSize: 12,
    color: "#0f172a",
    width: 220,
    textAlign: "center",
  },
  compute: {
    background: "#e7f5ff",
    border: "2px solid #1c7ed6",
    borderRadius: 12,
    padding: 6,
    fontWeight: 700,
    fontSize: 12,
    color: "#0b2e59",
    width: 170,
    textAlign: "center",
  },
  data: {
    background: "#fff0f6",
    border: "2px solid #a61e4d",
    borderRadius: 12,
    padding: 6,
    fontWeight: 700,
    fontSize: 12,
    color: "#4a0b2a",
    width: 170,
    textAlign: "center",
  },
  ops: {
    background: "#f3fff6",
    border: "2px solid #2f9e44",
    borderRadius: 12,
    padding: 6,
    fontWeight: 700,
    fontSize: 12,
    color: "#0f3d1f",
    width: 190,
    textAlign: "center",
  },
  note: {
    background: "#fffbeb",
    border: "2px solid #f59e0b",
    borderRadius: 10,
    padding: 6,
    fontWeight: 700,
    fontSize: 12,
    color: "#92400e",
    width: 200,
    textAlign: "center",
  },
  net: {
    background: "#e3fafc",
    border: "2px solid #1098ad",
    borderRadius: 12,
    padding: 6,
    fontWeight: 700,
    fontSize: 12,
    color: "#05333b",
    width: 170,
    textAlign: "center",
  },
};

function mkEdge(id, source, target, label, opts = {}) {
  return {
    id,
    source,
    target,
    label,
    type: "smoothstep",
    animated: opts?.animated ?? false,
    markerEnd: {
      type: MarkerType.ArrowClosed,
      width: 18,
      height: 18,
      color: opts?.style?.stroke ?? "#64748b",
    },
    labelStyle: { fontWeight: 700, fill: "#0f172a" },
    labelBgStyle: { fill: "#ffffff" },
    labelBgPadding: [6, 4],
    style: {
      strokeWidth: 4,
      stroke: "#64748b",
      ...(opts?.style ?? {}),
    },
    ...opts,
  };
}

export default function Project11FlowDiagram() {
  const nodes = useMemo(
    () => [
      // Background groups
      {
        id: "bg-legacy",
        position: { x: 60, y: 40 },
        data: { label: "" },
        style: {
          width: 500,
          height: 620,
          background: "#fef2f240",
          border: "3px solid #fca5a5",
          borderRadius: 16,
          zIndex: -1,
        },
        draggable: false,
        selectable: false,
      },
      {
        id: "bg-machinery",
        position: { x: 620, y: 40 },
        data: { label: "" },
        style: {
          width: 540,
          height: 620,
          background: "#eff6ff40",
          border: "3px solid #93c5fd",
          borderRadius: 16,
          zIndex: -1,
        },
        draggable: false,
        selectable: false,
      },
      {
        id: "bg-target",
        position: { x: 1220, y: 40 },
        data: { label: "" },
        style: {
          width: 540,
          height: 620,
          background: "#ecfdf340",
          border: "3px solid #86efac",
          borderRadius: 16,
          zIndex: -1,
        },
        draggable: false,
        selectable: false,
      },
      {
        id: "bg-cutover",
        position: { x: 1820, y: 40 },
        data: { label: "" },
        style: {
          width: 480,
          height: 620,
          background: "#f5f3ff40",
          border: "3px solid #c4b5fd",
          borderRadius: 16,
          zIndex: -1,
        },
        draggable: false,
        selectable: false,
      },

      // Section titles
      { id: "sec-legacy", position: { x: 200, y: 50 }, data: { label: "Legacy Fleet (KC15 / Wildfly)" }, style: nodeStyle.section },
      { id: "sec-machinery", position: { x: 780, y: 50 }, data: { label: "Migration Machinery" }, style: nodeStyle.section },
      { id: "sec-target", position: { x: 1380, y: 50 }, data: { label: "KC26 / Quarkus on ARM" }, style: nodeStyle.section },
      { id: "sec-cutover", position: { x: 1950, y: 50 }, data: { label: "Verify & Cut Over" }, style: nodeStyle.section },

      // Legacy column
      { id: "l1", position: { x: 220, y: 140 }, data: { label: "30 tenant realms (SAML / OIDC)" }, style: { ...nodeStyle.compute, width: 200 } },
      { id: "l2", position: { x: 220, y: 240 }, data: { label: "Custom SPI (persona SAML mapper, Java 8)" }, style: { ...nodeStyle.note, width: 210 } },
      { id: "l3", position: { x: 220, y: 350 }, data: { label: "RDS PostgreSQL (KC15)" }, style: nodeStyle.data },
      { id: "l4", position: { x: 220, y: 450 }, data: { label: "Realm + user exports (password hashes kept)" }, style: { ...nodeStyle.data, width: 210 } },
      { id: "l5", position: { x: 220, y: 560 }, data: { label: "Federated links + roles" }, style: nodeStyle.data },

      // Machinery column
      { id: "m1", position: { x: 800, y: 140 }, data: { label: "YAML per customer" }, style: nodeStyle.net },
      { id: "m2", position: { x: 800, y: 230 }, data: { label: "boto3 deployer" }, style: nodeStyle.ops },
      { id: "m3", position: { x: 800, y: 320 }, data: { label: "SPI ported to Java 21 (Quarkus API)" }, style: { ...nodeStyle.note, width: 210 } },
      { id: "m4", position: { x: 800, y: 430 }, data: { label: "S3 login theming (CSS-only)" }, style: nodeStyle.ops },
      { id: "m5", position: { x: 800, y: 520 }, data: { label: "Env-templated config (RFC 9207 logout fixes)" }, style: { ...nodeStyle.ops, width: 210 } },

      // Target column
      { id: "t1", position: { x: 1390, y: 140 }, data: { label: "ASG on ARM Graviton" }, style: nodeStyle.compute },
      { id: "t2", position: { x: 1390, y: 230 }, data: { label: "Rootless Quarkus install" }, style: nodeStyle.compute },
      { id: "t3", position: { x: 1390, y: 320 }, data: { label: "SPI self-installs at build" }, style: nodeStyle.note },
      { id: "t4", position: { x: 1390, y: 410 }, data: { label: "RDS PostgreSQL (KC26)" }, style: nodeStyle.data },
      { id: "t5", position: { x: 1390, y: 500 }, data: { label: "Splunk forwarding + login dashboard" }, style: { ...nodeStyle.ops, width: 210 } },
      { id: "t6", position: { x: 1390, y: 600 }, data: { label: "ALB preview target group" }, style: nodeStyle.net },

      // Cutover column
      { id: "c1", position: { x: 1960, y: 140 }, data: { label: "md5 diff: client / IdP secrets" }, style: { ...nodeStyle.note, width: 210 } },
      { id: "c2", position: { x: 1960, y: 240 }, data: { label: "Mapper-config diff (KC15 vs KC26)" }, style: { ...nodeStyle.note, width: 210 } },
      { id: "c3", position: { x: 1960, y: 340 }, data: { label: "Cutover runbook + checklist" }, style: nodeStyle.ops },
      { id: "c4", position: { x: 1960, y: 430 }, data: { label: "Blue-green TG swap" }, style: nodeStyle.net },
      { id: "c5", position: { x: 1960, y: 520 }, data: { label: "24h rollback window" }, style: nodeStyle.ops },
      { id: "c6", position: { x: 1960, y: 610 }, data: { label: "Days -> hours per customer" }, style: { ...nodeStyle.compute, width: 210 } },
    ],
    []
  );

  const edges = useMemo(() => {
    const red = { stroke: "#dc2626" };
    const blue = { stroke: "#2563eb" };
    const green = { stroke: "#16a34a" };
    const purple = { stroke: "#7c3aed" };

    return [
      // Legacy internal
      mkEdge("le1", "l1", "l2", "uses", { style: { ...red } }),
      mkEdge("le2", "l1", "l3", "persists", { style: { ...red } }),
      mkEdge("le3", "l3", "l4", "export", { style: { ...red } }),
      mkEdge("le4", "l4", "l5", "preserve", { style: { ...red } }),

      // Legacy -> Machinery
      mkEdge("lm1", "l2", "m3", "port (Wildfly -> Quarkus)", { style: { ...blue, strokeDasharray: "10 8" } }),
      mkEdge("lm2", "l4", "m2", "import input", { style: { ...blue } }),

      // Machinery internal
      mkEdge("mm1", "m1", "m2", "config-driven", { style: { ...blue } }),
      mkEdge("mm2", "m2", "m4", "theme per customer", { style: { ...blue } }),
      mkEdge("mm3", "m2", "m5", "stamp at build", { style: { ...blue } }),

      // Machinery -> Target
      mkEdge("mt1", "m2", "t1", "provision", { style: { ...green } }),
      mkEdge("mt2", "m3", "t3", "embed in pipeline", { style: { ...green } }),
      mkEdge("mt3", "m5", "t2", "configure", { style: { ...green } }),
      mkEdge("mt4", "m2", "t4", "import realms", { style: { ...green } }),

      // Target internal
      mkEdge("tt1", "t1", "t2", "runs", { style: { ...green } }),
      mkEdge("tt2", "t2", "t3", "loads SPI", { style: { ...green } }),
      mkEdge("tt3", "t2", "t4", "persists", { style: { ...green } }),
      mkEdge("tt4", "t2", "t5", "logs", { style: { ...green } }),
      mkEdge("tt5", "t1", "t6", "registered", { style: { ...green } }),

      // Target -> Cutover
      mkEdge("tc1", "t4", "c1", "checksum", { style: { ...purple } }),
      mkEdge("tc2", "t4", "c2", "diff mappers", { style: { ...purple } }),
      mkEdge("tc3", "t6", "c4", "preview verified", { style: { ...purple } }),

      // Cutover chain
      mkEdge("cc1", "c1", "c3", "gate", { style: { ...purple } }),
      mkEdge("cc2", "c2", "c3", "gate", { style: { ...purple } }),
      mkEdge("cc3", "c3", "c4", "swap", { style: { ...purple } }),
      mkEdge("cc4", "c4", "c5", "monitor", { style: { ...purple } }),
      mkEdge("cc5", "c5", "c6", "repeat x30", { style: { ...purple } }),
    ];
  }, []);

  return (
    <div style={{ width: "100%", height: "700px", background: "#ffffff" }}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        fitView
        fitViewOptions={{ padding: 0.08 }}
        defaultEdgeOptions={{
          type: "smoothstep",
          markerEnd: { type: MarkerType.ArrowClosed },
        }}
        minZoom={0.35}
        maxZoom={2}
        zoomOnScroll={false}
        zoomOnPinch={false}
        panOnScroll={false}
        preventScrolling={true}
      >
        <Background gap={18} size={1} color="#e5e7eb" />
        <MiniMap pannable zoomable />
        <Controls />
      </ReactFlow>
    </div>
  );
}
